/**
 * API client for Centertel backend (ennychat.com.br)
 * Handles tag validation, tag configs, and permissions for the energy sales flow.
 */
const CENTERTEL_URL = 'https://ennychat.com.br';

const centertelFetch = (path, options = {}) => {
  return fetch(`${CENTERTEL_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  }).then(r => r.json());
};

// Cache de tag configs (60s TTL)
let tagConfigsCache = { data: null, time: 0 };
const TAG_CONFIGS_TTL = 60_000;

/**
 * Busca todas as ConfigTagChatwoot do Centertel.
 * Retorna { [slug]: { times, contexto, requer_tags, bloqueada, exclusiva, ... } }
 */
export const getTagConfigs = async () => {
  if (tagConfigsCache.data && Date.now() - tagConfigsCache.time < TAG_CONFIGS_TTL) {
    return tagConfigsCache.data;
  }
  try {
    const result = await centertelFetch('/api/energia/config-tags/', {
      credentials: 'include',
    });
    const configs = result.data || result || [];
    const map = {};
    configs.forEach(cfg => {
      map[cfg.slug] = cfg;
    });
    tagConfigsCache = { data: map, time: Date.now() };
    return map;
  } catch {
    return tagConfigsCache.data || {};
  }
};

/**
 * Invalida o cache de tag configs.
 */
export const invalidateTagConfigs = () => {
  tagConfigsCache = { data: null, time: 0 };
};

// Cache de validação (30s TTL por tag+acao+team)
const validationCache = {};
const VALIDATION_TTL = 30_000;

/**
 * Valida se uma tag pode ser adicionada/removida.
 * @param {string} tag - slug da tag
 * @param {string} acao - 'adicionar' ou 'remover'
 * @param {string[]} labelsAtuais - tags atuais na conversa
 * @param {number|null} teamId - ID do time da conversa
 * @returns {Promise<{permitido: boolean, mensagem?: string, aviso?: string}>}
 */
export const validarTag = async (tag, acao, labelsAtuais, teamId) => {
  const cacheKey = `${tag}:${acao}:${teamId}`;
  const cached = validationCache[cacheKey];
  if (cached && Date.now() - cached.time < VALIDATION_TTL) {
    return cached.result;
  }

  try {
    const result = await centertelFetch('/api/energia/validar-tag/', {
      method: 'POST',
      body: JSON.stringify({ tag, acao, labels: labelsAtuais, team_id: teamId }),
    });
    validationCache[cacheKey] = { result, time: Date.now() };
    return result;
  } catch {
    return { permitido: true }; // Fail-open
  }
};

/**
 * Verifica se uma tag é permitida para o time da conversa.
 * Baseado nas configs carregadas (sem request).
 */
export const isTagAllowedForTeam = (tagSlug, teamId, tagConfigs) => {
  const cfg = tagConfigs[tagSlug];
  if (!cfg) return true; // Tag sem config = livre para todos
  if (!cfg.times || cfg.times.length === 0) return true; // Sem time = livre
  if (!teamId) return true; // Conversa sem time = permite tudo
  return cfg.times.some(t => t.id === teamId);
};
